import * as React from "react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/** A labeled field wrapper for the settings panels. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** A toggle row: label + description on the left, switch on the right. */
export function ToggleRow({
  label,
  description,
  defaultChecked,
}: {
  label: string;
  description?: string;
  defaultChecked?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {description && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">{description}</div>
        )}
      </div>
      <Switch defaultChecked={defaultChecked} />
    </div>
  );
}

/** A range slider with min/max captions and a live value badge. */
export function SliderRow({
  label,
  description,
  min,
  max,
  step,
  defaultValue,
  minLabel,
  maxLabel,
  format = (v) => String(v),
}: {
  label: string;
  description?: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  minLabel?: string;
  maxLabel?: string;
  format?: (v: number) => string;
}) {
  const [value, setValue] = React.useState(defaultValue);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        <span className="rounded-md border bg-muted/50 px-2 py-0.5 text-xs font-mono tabular-nums">
          {format(value)}
        </span>
      </div>
      {description && (
        <p className="text-[11px] text-muted-foreground">{description}</p>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
      />
      {(minLabel || maxLabel) && (
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
}

/** A segmented single-choice control (Vapi-style pill group). */
export function Segmented({
  label,
  description,
  options,
  defaultValue,
}: {
  label: string;
  description?: string;
  options: string[];
  defaultValue: string;
}) {
  const [value, setValue] = React.useState(defaultValue);
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium">{label}</div>
      {description && (
        <p className="text-[11px] text-muted-foreground">{description}</p>
      )}
      <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setValue(opt)}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors",
              value === opt
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
